import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Check } from 'lucide-react';
import { Button } from '../../../components';
import { useLang } from '../../../i18n';
import { useLeadModal } from '../../../contexts/LeadModalContext';
import useIntersectionFadeIn from '../../../hooks/useIntersectionFadeIn';

export default function HeroSection() {
  const { t } = useLang();
  const { openModal } = useLeadModal();
  const rootRef = useRef<HTMLElement>(null);
  const visible = useIntersectionFadeIn(rootRef, { threshold: 0.05 });

  const features = [t('home.hero.feat1'), t('home.hero.feat2'), t('home.hero.feat3')];

  const projects = [
    { tag: t('home.hero.proj_tag'), title: t('home.hero.proj_title'), status: t('home.hero.proj_status'), pct: 72 },
    { tag: t('home.hero.proj2_tag'), title: t('home.hero.proj2_title'), status: t('home.hero.proj2_status'), pct: 100 },
    { tag: t('home.hero.proj3_tag'), title: t('home.hero.proj3_title'), status: t('home.hero.proj3_status'), pct: 24 },
  ];

  return (
    <section ref={rootRef} className={`hero scroll-fade-in${visible ? ' scroll-fade-in--visible' : ''}`}>
      <div className="hero__pattern tech-pattern" aria-hidden="true" />
      <div className="container hero__grid">
        <div className="hero__content">
          <span className="eyebrow hero__eyebrow">{t('home.hero.eyebrow')}</span>
          <h1 className="hero__title">{t('home.hero.h1')}</h1>
          <p className="lead hero__lead">{t('home.hero.sub')}</p>

          <div className="hero__cta">
            <Button
              variant="primary"
              size="lg"
              onClick={() => openModal()}
              iconRight={<ArrowRight size={18} />}
            >
              {t('home.hero.cta1')}
            </Button>
            <Link to="/services" className="hero__cta-link">
              <Button variant="secondary" size="lg" iconRight={<ChevronRight size={18} />}>
                {t('home.hero.cta2')}
              </Button>
            </Link>
          </div>

          <ul className="hero__features">
            {features.map((label) => (
              <li key={label} className="hero__feature">
                <span className="hero__feature-icon" aria-hidden="true">
                  <Check size={14} strokeWidth={2.5} />
                </span>
                <span className="hero__feature-label">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="hero__visual" aria-hidden="true">
          <div className="hero__stack">
            {projects.map((p, idx) => (
              <article key={p.tag} className={`hero-card hero-card--${idx + 1} card-elevated`}>
                <header className="hero-card__head">
                  <span className="tech-label">{p.tag}</span>
                  <span className="hero-card__status">{p.status}</span>
                </header>
                <h3 className="hero-card__title">{p.title}</h3>
                <div className="hero-card__bar" role="presentation">
                  <span className="hero-card__bar-track" />
                  <span className="hero-card__bar-fill" style={{ width: `${p.pct}%` }} />
                </div>
                <footer className="hero-card__foot">
                  <span className="hero-card__pct">{p.pct}%</span>
                  <span className="hero-card__pct-label">progress</span>
                </footer>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
